import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet-async";
import Select from "react-select";
import { Country, State, City } from "country-state-city";
import { Info, Pencil } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Checkbox } from "components/ui/checkbox";
import api, { isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import swtAlert from "lib/sweetAlert";
import {
  required,
  validateName,
  validatePhone,
  validatePostalCode,
  validateText,
  validateChecked,
  validateForm as runFormValidation,
} from "lib/validation";
import { selectAuthUser } from "store/reducer";
import {
  mapAddressFormToApi,
  mapAuthUserToProfileForm,
  reactSelectClassNames,
  refreshAuthUser,
  withSelectOptionFallback,
} from "pages/settings/settingsHelper";

const BENEFICIAL_OWNER_KEYS = ["owner-1", "owner-2", "owner-3", "owner-4", "owner-5"];

function FieldError({ message }) {
  if (!message) return null;
  return <p className="text-xs text-destructive mt-1">{message}</p>;
}

FieldError.propTypes = {
  message: PropTypes.string,
};

function isCorporateUser(user) {
  return String(user?.investorType || "").toLowerCase() === "corporate";
}

function buildEmptyForm() {
  return {
    phone: "",
    firstName: "",
    lastName: "",
    companyName: "",
    line1: "",
    city: "",
    province: "",
    postalCode: "",
    country: "",
    isOver19Confirmed: false,
    beneficialOwners: ["", "", "", "", ""],
  };
}

export default function SettingsProfilePage() {
  const dispatch = useDispatch();
  const user = useSelector(selectAuthUser);
  const corporate = isCorporateUser(user);

  const [formData, setFormData] = useState(buildEmptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const applyUserToForm = useCallback(() => {
    setFormData(mapAuthUserToProfileForm(user, corporate));
    setErrors({});
  }, [user, corporate]);

  useEffect(() => {
    applyUserToForm();
  }, [applyUserToForm]);

  const countryOptions = useMemo(
    () =>
      Country.getAllCountries().map((c) => ({
        value: c.name,
        label: c.name,
      })),
    []
  );

  const selectedCountryIso = useMemo(() => {
    if (!formData.country) return "";
    return (
      Country.getAllCountries().find((c) => c.name === formData.country)
        ?.isoCode ?? ""
    );
  }, [formData.country]);

  const provinceOptions = useMemo(
    () =>
      selectedCountryIso
        ? State.getStatesOfCountry(selectedCountryIso).map((s) => ({
            value: s.name,
            label: s.name,
          }))
        : [],
    [selectedCountryIso]
  );

  const selectedProvinceIso = useMemo(() => {
    if (!selectedCountryIso || !formData.province) return "";
    return (
      State.getStatesOfCountry(selectedCountryIso).find(
        (s) => s.name === formData.province
      )?.isoCode ?? ""
    );
  }, [selectedCountryIso, formData.province]);

  const cityOptions = useMemo(() => {
    const fromLibrary =
      selectedCountryIso && selectedProvinceIso
        ? City.getCitiesOfState(selectedCountryIso, selectedProvinceIso).map(
            (c) => ({ value: c.name, label: c.name })
          )
        : [];
    return withSelectOptionFallback(fromLibrary, formData.city);
  }, [selectedCountryIso, selectedProvinceIso, formData.city]);

  const selectedCountry =
    countryOptions.find((o) => o.value === formData.country) || null;
  const selectedProvince =
    provinceOptions.find((o) => o.value === formData.province) || null;
  const selectedCity =
    cityOptions.find((o) => o.value === formData.city) || null;

  const clearError = useCallback((field) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: "" } : prev));
  }, []);

  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      if (name === "postalCode") {
        setFormData((prev) => ({
          ...prev,
          postalCode: value.replace(/\s+/g, "").toUpperCase(),
        }));
      } else if (name === "phone") {
        setFormData((prev) => ({
          ...prev,
          phone: value.replace(/\D/g, ""),
        }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
      clearError(name);
    },
    [clearError]
  );

  const handleBeneficialOwnerChange = useCallback((index, value) => {
    setFormData((prev) => {
      const next = [...prev.beneficialOwners];
      next[index] = value;
      return { ...prev, beneficialOwners: next };
    });
  }, []);

  const validateForm = useCallback(() => {
    const schema = {
      firstName: (v) => validateName(v, { label: "First name" }),
      lastName: (v) => validateName(v, { label: "Last name" }),
      phone: (v) => validatePhone(v),
      country: required("Country"),
      province: required("Province/State"),
      city: required("City"),
      line1: (v) => validateText(v, { label: "Address", min: 3, max: 120 }),
      postalCode: (v) => validatePostalCode(v, selectedCountryIso),
      isOver19Confirmed: (v) =>
        validateChecked(v, {
          message: "Please confirm that you are over 18 years old.",
        }),
    };
    if (corporate) {
      schema.companyName = (v) =>
        validateText(v, { label: "Company name", min: 2, max: 120 });
    }
    const newErrors = runFormValidation(formData, schema);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [corporate, formData, selectedCountryIso]);

  const buildProfilePayload = useCallback(() => {
    const address = mapAddressFormToApi(formData);

    const body = { phone: formData.phone.trim() };

    if (corporate) {
      body.firstName = formData.firstName.trim();
      body.lastName = formData.lastName.trim();
      body.isOver19Confirmed = formData.isOver19Confirmed;
      body.corporateProfile = {
        companyName: formData.companyName.trim(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        registeredAddress: address,
        beneficialOwners: formData.beneficialOwners
          .map((o) => String(o ?? "").trim())
          .filter(Boolean),
      };
    } else {
      body.individualProfile = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        isOver19Confirmed: formData.isOver19Confirmed,
        residentialAddress: address,
      };
    }

    return body;
  }, [corporate, formData]);

  const handleCancelEdit = useCallback(() => {
    applyUserToForm();
    setIsEditing(false);
  }, [applyUserToForm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditing) return;
    if (!validateForm()) {
      await swtAlert.warning(
        "Check your details",
        "Please fix the highlighted fields before saving."
      );
      return;
    }

    setSaving(true);
    try {
      const resp = await api.patch(API_ENDPOINTS.profile, buildProfilePayload());
      if (!isSuccessResponse(resp)) {
        await swtAlert.error(
          "Update failed",
          resp?.message || "Could not update your profile."
        );
        return;
      }
      await refreshAuthUser(dispatch);
      setIsEditing(false);
      await swtAlert.success(
        "Profile updated",
        resp?.message || "Your account details have been saved."
      );
    } catch (err) {
      await swtAlert.error(
        "Update failed",
        err?.message || "Could not update your profile."
      );
    } finally {
      setSaving(false);
    }
  };

  const fieldStack = "space-y-2";
  const fieldDisabled = !isEditing;
  const selectExtraDisabledCountry = fieldDisabled;
  const selectExtraDisabledProvince = fieldDisabled || !formData.country;
  const selectExtraDisabledCity = fieldDisabled || !formData.province;

  return (
    <>
      <Helmet>
        <title>Account information | Fortify</title>
      </Helmet>
      <div>
        <div className="flex flex-wrap items-start justify-between gap-4 max-w-xl">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                Account Information
              </h1>
              {isEditing ? null : (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0 text-muted-foreground hover:text-foreground"
                  onClick={() => setIsEditing(true)}
                  aria-label="Edit profile"
                >
                  <Pencil className="h-5 w-5" />
                </Button>
              )}
            </div>
            <div className="mt-2 h-0.5 w-full bg-accent" aria-hidden />
          </div>
        </div>

        <div className="mt-6 flex items-start gap-2 rounded-md border border-border bg-card/80 px-3 py-2.5 text-sm text-muted-foreground max-w-xl">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden />
          <p>
            Your profile must be fully complete in order to make an investment.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 max-w-xl space-y-6">
          {corporate ? (
            <div className={fieldStack}>
              <Label htmlFor="companyName">Company Name</Label>
              <Input
                id="companyName"
                name="companyName"
                placeholder="Company Name"
                value={formData.companyName}
                onChange={handleInputChange}
                disabled={fieldDisabled}
                aria-invalid={!!errors.companyName}
              />
              <FieldError message={errors.companyName} />
            </div>
          ) : null}

          <div className={fieldStack}>
            <Label htmlFor="firstName">First Name</Label>
            <Input
              id="firstName"
              name="firstName"
              placeholder="First Name"
              value={formData.firstName}
              onChange={handleInputChange}
              disabled={fieldDisabled}
              aria-invalid={!!errors.firstName}
            />
            <FieldError message={errors.firstName} />
          </div>

          <div className={fieldStack}>
            <Label htmlFor="lastName">Last Name</Label>
            <Input
              id="lastName"
              name="lastName"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={handleInputChange}
              disabled={fieldDisabled}
              aria-invalid={!!errors.lastName}
            />
            <FieldError message={errors.lastName} />
          </div>

          <div className={fieldStack}>
            <Label htmlFor="email-not-editable">Email</Label>
            <Input
              id="email-not-editable"
              value={user?.email || ""}
              readOnly
              disabled
              className="bg-muted/50"
            />
          </div>

          <div className={fieldStack}>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              pattern="[0-9]*"
              placeholder="Phone Number: 6042224444"
              value={formData.phone}
              onChange={handleInputChange}
              disabled={fieldDisabled}
              aria-invalid={!!errors.phone}
            />
            <FieldError message={errors.phone} />
          </div>

          {/* Order: Country, Province/State, City, Address, Postal — matches SignUpFormPage */}
          <div className={fieldStack}>
            <Label htmlFor="country-select">Country</Label>
            <Select
              inputId="country-select"
              options={countryOptions}
              value={selectedCountry}
              onChange={(opt) => {
                setFormData((prev) => ({
                  ...prev,
                  country: opt?.value || "",
                  province: "",
                  city: "",
                }));
                clearError("country");
              }}
              placeholder="Select Country"
              isClearable
              isDisabled={selectExtraDisabledCountry}
              classNames={reactSelectClassNames}
            />
            <FieldError message={errors.country} />
          </div>

          <div className={fieldStack}>
            <Label htmlFor="province-select">Province/State</Label>
            <Select
              inputId="province-select"
              options={provinceOptions}
              value={selectedProvince}
              onChange={(opt) => {
                setFormData((prev) => ({
                  ...prev,
                  province: opt?.value || "",
                  city: "",
                }));
                clearError("province");
              }}
              placeholder={
                formData.country ? "Select Province/State" : "Select country first"
              }
              isDisabled={selectExtraDisabledProvince}
              isClearable
              classNames={reactSelectClassNames}
            />
            <FieldError message={errors.province} />
          </div>

          <div className={fieldStack}>
            <Label htmlFor="city-select">City</Label>
            <Select
              inputId="city-select"
              options={cityOptions}
              value={selectedCity}
              onChange={(opt) => {
                setFormData((prev) => ({ ...prev, city: opt?.value || "" }));
                clearError("city");
              }}
              placeholder={
                formData.province ? "Select City" : "Select province/state first"
              }
              isDisabled={selectExtraDisabledCity}
              isClearable
              classNames={reactSelectClassNames}
            />
            <FieldError message={errors.city} />
          </div>

          <div className={fieldStack}>
            <Label htmlFor="line1">Address</Label>
            <Input
              id="line1"
              name="line1"
              placeholder="Street address"
              value={formData.line1}
              onChange={handleInputChange}
              disabled={fieldDisabled}
              aria-invalid={!!errors.line1}
            />
            <FieldError message={errors.line1} />
          </div>

          <div className={fieldStack}>
            <Label htmlFor="postalCode">Postal Code</Label>
            <Input
              id="postalCode"
              name="postalCode"
              placeholder="Postal Code"
              value={formData.postalCode}
              onChange={handleInputChange}
              disabled={fieldDisabled}
              aria-invalid={!!errors.postalCode}
            />
            <FieldError message={errors.postalCode} />
          </div>

          {corporate ? (
            <div className="space-y-4">
              <p className="text-sm font-medium text-foreground">Beneficial owners</p>
              {formData.beneficialOwners.map((owner, idx) => (
                <div key={BENEFICIAL_OWNER_KEYS[idx]} className={fieldStack}>
                  <Label htmlFor={`beneficialOwner-${idx}`}>
                    Beneficial Owner {idx + 1}
                  </Label>
                  <Input
                    id={`beneficialOwner-${idx}`}
                    placeholder={`Beneficial Owner ${idx + 1}`}
                    value={owner}
                    onChange={(e) =>
                      handleBeneficialOwnerChange(idx, e.target.value)
                    }
                    disabled={fieldDisabled}
                  />
                </div>
              ))}
            </div>
          ) : null}

          <div className="flex items-start gap-3 pt-2">
            <Checkbox
              id="isOver19Confirmed"
              checked={formData.isOver19Confirmed}
              onCheckedChange={(v) => {
                setFormData((prev) => ({ ...prev, isOver19Confirmed: v === true }));
                if (v) clearError("isOver19Confirmed");
              }}
              disabled={true}
              className="mt-1"
              aria-invalid={!!errors.isOver19Confirmed}
            />
            <Label
              htmlFor="isOver19Confirmed"
              className="font-normal leading-5 cursor-pointer text-foreground"
            >
              I confirm that I am over 18 years old.
            </Label>
          </div>
          <FieldError message={errors.isOver19Confirmed} />

          {isEditing ? (
            <div className="flex flex-wrap gap-3 pt-4">
              <Button type="submit" size="lg" disabled={saving}>
                {saving ? "Updating…" : "Update Profile"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="lg"
                disabled={saving}
                onClick={handleCancelEdit}
              >
                Cancel
              </Button>
            </div>
          ) : null}
        </form>
      </div>
    </>
  );
}
